import {
  BF_DONATION_FIELDS,
  EIS_AMOUNT_FIELDS,
  PL_SUPPLEMENT_FIELDS,
  SIGNED_AMOUNT_FIELDS,
  TAX_ADJUSTMENT_GROUPS,
  getDataFormCSFieldMeta,
} from "./formCSDataFormFields";
import { isFormCSBasisPeriodLongerThan12Months } from "./formCSBasisPeriodUtils";

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ALLOWED_STRING_REGEX = /^[a-zA-Z0-9'‘@#()\-,./&+_ ]*$/;

const requiredNumberFields = [
  ...TAX_ADJUSTMENT_GROUPS.flatMap((group) => group.keys),
  ...BF_DONATION_FIELDS,
  ...PL_SUPPLEMENT_FIELDS,
  "expRD",
  "expRDSG",
  "c1_EnhancedEISDed",
  "foreignAssetsSaleGainLoss",
  "foreignSourceSaleGainsRemit",
];

const optionalNumberFields = [
  "unutilCALDClaimS23S37",
  "enhanceDeductRD",
  ...EIS_AMOUNT_FIELDS,
];

const standardYesNoYnFields = [
  "sholderChange",
  "uCALDChangePrinAct",
  "fullTxX",
];

const ptisForeignYesNoYnFields = ["ptisDonInd", "foreignSourceSaleNotTax"];

const signedAmountFields = SIGNED_AMOUNT_FIELDS;

const unique = (values) => [...new Set(values)];

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const isValidIsoDate = (value) => {
  if (isBlank(value) || !DATE_REGEX.test(String(value))) return false;

  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  );
};

const fieldLabel = (field) =>
  getDataFormCSFieldMeta(field)?.label || field.replace(/([A-Z])/g, " $1");

const optionLabel = (values) =>
  values
    .map((value) => {
      if (value === "Y") return "Yes";
      if (value === "N") return "No";
      if (value === "1" || value === 1) return "Yes";
      if (value === "2" || value === 2) return "No";
      if (value === "NA") return "N/A";
      return String(value);
    })
    .join(", ");

const normalizePath = (field) =>
  String(field || "")
    .replace(/^dataFormCS\./, "")
    .replace(/^payload\.dataFormCS\./, "");

const pushError = (errors, field, message) => {
  errors.push({
    field,
    path: `dataFormCS.${field}`,
    label: fieldLabel(field),
    message,
  });
};

const requireDate = (errors, data, field, message) => {
  if (isBlank(data[field])) {
    pushError(errors, field, message || `Enter ${fieldLabel(field)}.`);
    return;
  }
  if (!isValidIsoDate(data[field])) {
    pushError(errors, field, "Enter a valid date in YYYY-MM-DD format.");
  }
};

const requireNumber = (errors, data, field) => {
  if (!isFiniteNumber(data[field])) {
    pushError(errors, field, `Enter a valid number for ${fieldLabel(field)}.`);
  }
};

const validateOptionalDate = (errors, data, field) => {
  if (!isBlank(data[field]) && !isValidIsoDate(data[field])) {
    pushError(errors, field, "Enter a valid date in YYYY-MM-DD format.");
  }
};

const validateOptionalNumber = (errors, data, field) => {
  if (data[field] !== undefined && !isFiniteNumber(data[field])) {
    pushError(errors, field, `Enter a valid number for ${fieldLabel(field)}.`);
  }
};

const validatePositiveAmount = (errors, data, field) => {
  if (
    isFiniteNumber(data[field]) &&
    !signedAmountFields.has(field) &&
    data[field] < 0
  ) {
    pushError(errors, field, `${fieldLabel(field)} must be zero or positive.`);
  }
};

const requireDropdownNumber = (errors, data, field, values, message) => {
  if (!values.includes(Number(data[field]))) {
    pushError(
      errors,
      field,
      message || `Select one of: ${optionLabel(values)}.`
    );
  }
};

const validateOptionalDropdownNumber = (errors, data, field, values) => {
  if (!isBlank(data[field]) && Number(data[field]) !== 0) {
    requireDropdownNumber(errors, data, field, values);
  }
};

const requireDropdownString = (errors, data, field, values, message) => {
  if (!values.includes(data[field])) {
    pushError(
      errors,
      field,
      message || `Select one of: ${optionLabel(values)}.`
    );
  }
};

const requireText = (errors, data, field, message) => {
  if (isBlank(data[field])) {
    pushError(errors, field, message || `Enter ${fieldLabel(field)}.`);
  } else if (!ALLOWED_STRING_REGEX.test(String(data[field]))) {
    pushError(errors, field, `${fieldLabel(field)} contains unsupported characters.`);
  }
};

const validateOptionalText = (errors, data, field) => {
  if (!isBlank(data[field]) && !ALLOWED_STRING_REGEX.test(String(data[field]))) {
    pushError(errors, field, `${fieldLabel(field)} contains unsupported characters.`);
  }
};

const validateOptionalDropdownString = (errors, data, field, values) => {
  if (!isBlank(data[field]) && !values.includes(data[field])) {
    pushError(errors, field, `Select one of: ${optionLabel(values)}.`);
  }
};

export const validateFormCSDataForm = (dataFormCS = {}) => {
  const errors = [];
  const isBasisPeriodOver12Months =
    isFormCSBasisPeriodLongerThan12Months(dataFormCS);

  requireDropdownNumber(
    errors,
    dataFormCS,
    "sctpInd",
    [1, 2],
    "Select Yes or No."
  );
  requireDate(errors, dataFormCS, "basisPeriodFrom", "Enter the basis period start date.");
  requireDate(errors, dataFormCS, "basisPeriodTo", "Enter the basis period end date.");
  if (isBasisPeriodOver12Months) {
    requireDropdownNumber(
      errors,
      dataFormCS,
      "fsPrep",
      [1, 2],
      "Select whether 1 or 2 sets of accounts were prepared."
    );
  } else {
    validateOptionalDropdownNumber(errors, dataFormCS, "fsPrep", [1, 2]);
  }

  if (Number(dataFormCS.fsPrep) === 2) {
    requireDate(
      errors,
      dataFormCS,
      "firstFYE",
      "Enter the financial year end date."
    );
  } else {
    validateOptionalDate(errors, dataFormCS, "firstFYE");
  }
  requireDropdownNumber(
    errors,
    dataFormCS,
    "appStockConvAsset",
    [1, 2],
    "Select Yes or No."
  );

  unique(requiredNumberFields).forEach((field) =>
    requireNumber(errors, dataFormCS, field)
  );
  unique(optionalNumberFields).forEach((field) =>
    validateOptionalNumber(errors, dataFormCS, field)
  );

  Object.keys(dataFormCS).forEach((field) => {
    validatePositiveAmount(errors, dataFormCS, field);
  });

  if (Number(dataFormCS.sctpInd) === 1) {
    requireText(errors, dataFormCS, "sctpName", "Enter the SCTP reviewer's name.");
    requireText(errors, dataFormCS, "sctpNo", "Enter the SCTP membership number.");
  } else {
    validateOptionalText(errors, dataFormCS, "sctpName");
    validateOptionalText(errors, dataFormCS, "sctpNo");
  }

  if (Number(dataFormCS.fsPrep) === 2) {
    requireDate(
      errors,
      dataFormCS,
      "prevFYE",
      "Enter the previous financial year end date."
    );
  } else {
    validateOptionalDate(errors, dataFormCS, "prevFYE");
  }

  standardYesNoYnFields.forEach((field) => {
    requireDropdownString(
      errors,
      dataFormCS,
      field,
      ["Y", "N", "NA"],
      "Select Yes, No, or Not Applicable."
    );
  });

  ptisForeignYesNoYnFields.forEach((field) => {
    requireDropdownString(
      errors,
      dataFormCS,
      field,
      ["Y", "N", "NA", "3"],
      "Select Yes, No, Not Applicable, or keep the existing value."
    );
  });

  const isEisClaimed =
    dataFormCS.eis_ClaimCashPayout === "1" ||
    dataFormCS.eis_ClaimDedAll === "1";
  if (isEisClaimed) {
    requireDropdownString(
      errors,
      dataFormCS,
      "eis_ClaimCashPayout",
      ["1", "2"],
      "Select Yes or No for the EIS cash payout claim, or clear both EIS claims as Not Applicable."
    );
    requireDropdownString(
      errors,
      dataFormCS,
      "eis_ClaimDedAll",
      ["1", "2"],
      "Select Yes or No for the EIS deduction claim, or clear both EIS claims as Not Applicable."
    );
    EIS_AMOUNT_FIELDS.forEach((field) => requireNumber(errors, dataFormCS, field));
  } else {
    validateOptionalDropdownString(errors, dataFormCS, "eis_ClaimCashPayout", [
      "1",
      "2",
      "",
    ]);
    validateOptionalDropdownString(errors, dataFormCS, "eis_ClaimDedAll", [
      "1",
      "2",
      "",
    ]);
  }

  const fieldErrors = errors.reduce((acc, error) => {
    if (!acc[error.field]) acc[error.field] = error.message;
    return acc;
  }, {});

  return { errors, fieldErrors, isValid: errors.length === 0 };
};

export const normalizeFormCSValidationErrors = (validationErrors = []) => {
  const normalized = [];

  validationErrors.forEach((item) => {
    const field = normalizePath(item?.field);
    if (!field) return;
    const message = item?.message || "";
    const label = fieldLabel(field);

    normalized.push({
      field,
      path: `dataFormCS.${field}`,
      label,
      message,
    });
  });

  const byField = new Map();
  normalized.forEach((error) => {
    if (!byField.has(error.field)) byField.set(error.field, error);
  });

  return [...byField.values()];
};

export const formatFormCSValidationErrors = (errors = []) => {
  if (!errors.length) return "";

  const visibleErrors = errors.slice(0, 8);
  const remaining = errors.length - visibleErrors.length;
  const lines = visibleErrors.map(
    (error) => `- ${error.label}: ${error.message}`
  );

  return [
    "Please review the highlighted Form C-S fields before submitting.",
    "",
    ...lines,
    ...(remaining > 0 ? [`- ${remaining} more field${remaining === 1 ? "" : "s"} to review.`] : []),
  ].join("\n");
};
